import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { Button, Popover, Stack, Typography } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getCompanyUploadsListInfiniteQueryOptions, type Upload } from "@/api/wai/wai3Api";

const dateFmt = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const formatTimestamp = (timestamp?: string | null) => {
  if (!timestamp) return "Ingen tilgjengelige";
  return dateFmt.format(new Date(timestamp));
};

const formatCoverage = (upload?: Upload) => {
  if (!upload) return "Ingen tilgjengelige";
  if (upload.daterange?.end)
    return `dekker t.o.m. ${dateFmt.format(new Date(upload.daterange.end))}`;
  return formatTimestamp(upload.timestamp);
};

const UploadInfo = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { data: latestStatePage } = useInfiniteQuery(
    getCompanyUploadsListInfiniteQueryOptions({
      upload_type: "states",
      order: ["-timestamp"],
      page_size: 1,
    }),
  );
  const { data: latestOutagePage } = useInfiniteQuery(
    getCompanyUploadsListInfiniteQueryOptions({
      upload_type: "outages",
      order: ["-timestamp"],
      page_size: 1,
    }),
  );

  const latestStateUpload = latestStatePage?.pages?.[0]?.results?.[0];
  const latestOutageUpload = latestOutagePage?.pages?.[0]?.results?.[0];

  // Using uploads.at(0) might return other types than those above (eg. crittable)
  const latestUpload = useMemo(() => {
    const candidates = [latestStateUpload, latestOutageUpload];
    if (candidates.length === 0) return undefined;
    return candidates.reduce((a, b) => {
      const ta = a?.timestamp ? Date.parse(a.timestamp) : -Infinity;
      const tb = b?.timestamp ? Date.parse(b.timestamp) : -Infinity;
      return ta >= tb ? a : b;
    });
  }, [latestStateUpload, latestOutageUpload]);

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        startIcon={<HistoryRoundedIcon fontSize="small" />}
        sx={(theme) => ({
          color: theme.vars.palette.text.secondary,
          textTransform: "none",
          px: 2.5,
        })}
      >
        Siste opplasting:{" "}
        <time aria-label="Siste opplastingsdato" dateTime={latestUpload?.timestamp ?? undefined}>
          {formatTimestamp(latestUpload?.timestamp)}
        </time>
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack spacing={1} sx={{ p: 2 }}>
          <Typography variant="body2">Tilstand: {formatCoverage(latestStateUpload)}</Typography>
          <Typography variant="body2">Avbrudd: {formatCoverage(latestOutageUpload)}</Typography>
        </Stack>
      </Popover>
    </>
  );
};

export default UploadInfo;
